/**
 * The "service program" card used on service detail pages (AEO, GEO, AEO+GEO,
 * digital marketing, Bengali service detail) and the /services list.
 * Compose it from the parts below so each page keeps its own copy and extras:
 *
 *   <ProgramCard>
 *     <ProgramIntro label="Service" title=… tagline=…>{cta}</ProgramIntro>
 *     <ProgramDetails deliverables=… outcomes=…>
 *       <ProgramRow heading="People also ask">…</ProgramRow>
 *     </ProgramDetails>
 *   </ProgramCard>
 */
import type { ReactNode } from "react";
import { Check } from "lucide-react";
import { Reveal } from "@/components/Reveal";

/** Bordered card in the page container; English pages fade it in. */
export function ProgramCard({ reveal = true, children }: { reveal?: boolean; children: ReactNode }) {
  const card = (
    <div className="bg-background border border-border rounded-3xl p-10 lg:p-16 grid lg:grid-cols-12 gap-10">
      {children}
    </div>
  );
  return <div className="container-x">{reveal ? <Reveal>{card}</Reveal> : card}</div>;
}

/**
 * Left column: small label, program name, tagline, then any extra paragraphs
 * or call-to-action passed as children. Bengali titles need a taller line height.
 */
export function ProgramIntro({
  label,
  title,
  tagline,
  lang = "en",
  children,
}: {
  label: ReactNode;
  title: ReactNode;
  tagline: ReactNode;
  lang?: "en" | "bn";
  children?: ReactNode;
}) {
  return (
    <div className="lg:col-span-5">
      <div className="text-xs uppercase tracking-[0.2em] text-primary">{label}</div>
      <h2 className={`mt-4 font-display text-4xl lg:text-5xl ${lang === "bn" ? "leading-[1.15]" : "leading-[1.05]"}`}>
        {title}
      </h2>
      <p className="mt-6 text-lg text-muted-foreground">{tagline}</p>
      {children}
    </div>
  );
}

const SUBHEADING = "text-sm uppercase tracking-[0.2em] text-muted-foreground mb-4";

/**
 * Right column: ticked deliverables and highlighted outcomes side by side,
 * followed by full-width rows passed as children.
 */
export function ProgramDetails({
  deliverables,
  outcomes,
  headings = { deliverables: "Deliverables", outcomes: "What it aims for" },
  children,
}: {
  deliverables: readonly string[];
  outcomes: readonly string[];
  headings?: { deliverables: string; outcomes: string };
  children?: ReactNode;
}) {
  return (
    <div className="lg:col-span-7 grid sm:grid-cols-2 gap-10">
      <div>
        <h3 className={SUBHEADING}>{headings.deliverables}</h3>
        <ul className="space-y-3">
          {deliverables.map((d) => (
            <li key={d} className="flex items-start gap-3 text-sm">
              <Check size={16} className="text-primary mt-1 flex-none" />
              <span>{d}</span>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <h3 className={SUBHEADING}>{headings.outcomes}</h3>
        <ul className="space-y-3">
          {outcomes.map((o) => (
            <li key={o} className="text-sm">
              <span className="text-gradient font-display text-lg block">{o}</span>
            </li>
          ))}
        </ul>
      </div>
      {children}
    </div>
  );
}

/** Full-width row under the lists (FAQ, live citations, …). */
export function ProgramRow({
  heading,
  spaced = false,
  children,
}: {
  heading: string;
  /** Adds a little extra space above the row. */
  spaced?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={spaced ? "sm:col-span-2 mt-2" : "sm:col-span-2"}>
      <h3 className={SUBHEADING}>{heading}</h3>
      {children}
    </div>
  );
}
